import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import Box from "@mui/material/Box";
import Slide from "@mui/material/Slide";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { HighlightedAppLocationViewCounts } from "../api/faq/getHighlightedAppLocationViews";
import { HighlightedFaqViewCounts } from "../api/faq/getHighlightedFaqViews";
import { setHighlightedAppLocationView } from "../api/faq/setHighlightedAppLocationView";
import { setHighlightedFaqViews } from "../api/faq/setHighlightedFaqView";
import {
  addFaqModalParamsToHref,
  addFaqParamsToSearchParams,
  hasHighlightedAppLocationViews,
  hasHighlightedFaqs,
} from "../helpers/faq";
import { addModalToHref, replaceModalParam } from "../helpers/routes";
import { useAuth } from "../providers/AuthProvider";
import { CRMAppLocationIdentifier, FaqAppLocation } from "../types/Faq";
import APHighlightTooltip from "./APHighlightTooltip";

export type FaqContextIds = {
  quoteId?: string;
  opportunityId?: string;
  dossierId?: string;
};

interface AppLocationProps {
  children: ReactNode;
  appLocations: FaqAppLocation[];
  appLocationIdentifier: CRMAppLocationIdentifier;
  highlightedFaqViews: HighlightedFaqViewCounts;
  highlightedAppLocationViews: HighlightedAppLocationViewCounts;
  contextIds?: FaqContextIds;
  hideIfNoFaqs?: boolean;
  hideHighlight?: boolean;
  triggerOnLoad?: boolean;
  persistentModal?: boolean;
}

const AppLocation = ({
  children,
  appLocations,
  appLocationIdentifier,
  highlightedFaqViews,
  highlightedAppLocationViews,
  contextIds = {},
  hideIfNoFaqs,
  hideHighlight,
  triggerOnLoad,
  persistentModal = false,
}: AppLocationProps) => {
  const { getScopedPageUrl } = useAuth();
  const navigate = useNavigate();
  const [hasEnteredViewport, setHasEnteredViewport] = useState(false);
  const hasEntered = useRef(false); // Track if it has already been set
  const [hasDisplayedMaximised, setHasDisplayedMaximised] = useState(false);
  const linkRef = useRef<HTMLAnchorElement | null>(null);

  const elementRef = useRef<HTMLDivElement | null>(null);

  const appLocation = appLocations.find(
    (location) => location.appLocationIdentifier === appLocationIdentifier
  );
  const hasFaqs = appLocation && appLocation.faqCount > 0;
  // Determine highlight state
  const highlightedFaq = appLocation?.highlightedFaq ?? null;
  const highlightedText = appLocation?.highlightedText ?? null;

  let linkTo = "";
  let [tooltipSearchParams] = useSearchParams();

  if (appLocation) {
    // Add modal param
    linkTo = addModalToHref(
      getScopedPageUrl("modalAppLocation", appLocation.id),
      window.location.href
    );

    linkTo = addFaqModalParamsToHref(
      linkTo,
      appLocation.id,
      contextIds,
      persistentModal
    );
    tooltipSearchParams = addFaqParamsToSearchParams(
      tooltipSearchParams,
      appLocation.id,
      highlightedFaq?.faqApplocationId || null,
      contextIds
    );
  }

  const shouldShowHighlightedFaq =
    !hideHighlight &&
    highlightedFaq &&
    hasHighlightedFaqs(
      appLocations,
      [appLocationIdentifier],
      highlightedFaqViews
    );
  const shouldShowHighlightedText =
    !hideHighlight &&
    !!highlightedText &&
    !shouldShowHighlightedFaq &&
    hasHighlightedAppLocationViews(
      appLocations,
      [appLocationIdentifier],
      highlightedAppLocationViews
    );
  const shouldShowHighlight =
    shouldShowHighlightedFaq || shouldShowHighlightedText;

  const showMaximisedHighlight =
    shouldShowHighlight && hasEnteredViewport && !hasDisplayedMaximised;
  const showMinimisedHighlight =
    !hideHighlight && highlightedFaq && !showMaximisedHighlight;

  // Detect when Faq Link is in view port
  useEffect(() => {
    if (!shouldShowHighlight || !elementRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only set the first time it enters the viewport
        if (entry.isIntersecting && !hasEntered.current) {
          hasEntered.current = true;
          setHasEnteredViewport(entry.isIntersecting);
        }
      },
      {
        root: null,
        rootMargin: "-10% 0px -20% 0px",
        threshold: 1,
      }
    );

    const el = elementRef.current;

    let raf1 = 0;
    let raf2 = 0;

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        observer.observe(el);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      observer.unobserve(el);
      observer.disconnect();
    };
  }, [shouldShowHighlight]);

  const { mutateAsync: mutateAsyncFaq } = setHighlightedFaqViews();
  const { mutateAsync: mutateAsyncAppLocation } =
    setHighlightedAppLocationView();

  useEffect(() => {
    if (showMaximisedHighlight) {
      const timer = setTimeout(() => {
        if (highlightedFaq) {
          setHasDisplayedMaximised(true);
          mutateAsyncFaq(highlightedFaq.id);
        } else if (highlightedText) {
          setHasDisplayedMaximised(true);
          mutateAsyncAppLocation(appLocationIdentifier);
        }
      }, 15000);

      return () => clearTimeout(timer);
    }
  }, [showMaximisedHighlight]);

  // Auto click link if triggerOnLoad is true
  useEffect(() => {
    if (triggerOnLoad && linkRef.current) {
      linkRef.current.click();
    }
  }, [triggerOnLoad, linkRef.current]);

  // Handle tooltip click
  const onTooltipClick = () => {
    if (highlightedFaq) {
      const newSearchParams = replaceModalParam(
        getScopedPageUrl("modalFaq", highlightedFaq.id),
        tooltipSearchParams
      );

      navigate(`?${newSearchParams.toString()}`, { replace: true });
    }
  };
  const tooltipText = highlightedFaq
    ? highlightedFaq.question
    : highlightedText ?? "";

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          pointerEvents: "none",
        }}
        className={`productTour-${appLocationIdentifier}`}
      />
      {hasFaqs || !!highlightedText ? (
        <APHighlightTooltip
          text={tooltipText}
          isOpen={showMaximisedHighlight}
          onClick={highlightedFaq ? onTooltipClick : undefined}
          showInfoIcon={!!highlightedFaq}
        >
          <Box sx={{ position: "relative" }} ref={elementRef}>
            {showMinimisedHighlight && (
              <Box
                sx={{ overflow: "hidden", position: "absolute", right: -14 }}
              >
                <Link to={linkTo} replace={true}>
                  <Slide in={true} timeout={500} direction="right">
                    <Box sx={{ display: "flex" }}>
                      <ChatBubbleIcon
                        fontSize="small"
                        sx={{
                          color: "tertiaryGreen.main",
                          fontSize: "0.7rem",
                        }}
                      />
                    </Box>
                  </Slide>
                </Link>
              </Box>
            )}

            {hasFaqs ? (
              <Link to={linkTo} replace={true} ref={linkRef}>
                {children}
              </Link>
            ) : (
              children
            )}
          </Box>
        </APHighlightTooltip>
      ) : hideIfNoFaqs ? (
        <></>
      ) : (
        children
      )}
    </Box>
  );
};

export default AppLocation;
